import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CatalogHomeResponse,
  CatalogItem,
  ContinueWatchingResponse,
  ProgressEntry,
  SearchResponse,
} from '@lumos/contracts';
import {
  awaitContinueWatchingSync,
  getContinueWatching,
  getHistory,
  getHomeCatalog,
  markWatched,
  removeFromHistory,
  resetProgress,
  search,
} from '../api/endpoints.js';
import { Card } from '../components/Card.js';
import type { InputVia } from '../components/input-modality.js';
import { LumosMark, PlayIcon } from '../components/icons.js';
import { stageArtImage, tinyImage, washImage } from '../components/images.js';
import { SearchField } from '../components/SearchField';
import { TitleHeading, loadTitleLogo, preloadTitleLogo, useTitleLogo } from '../components/TitleHeading.js';
import { SectionWash, useWashItem } from '../components/SectionWash.js';
import { TitleStage, preloadStage, type StageSource } from '../components/TitleStage.js';
import { useInfiniteScroll } from '../components/useInfiniteScroll.js';
import { Loader } from '../components/Loader.js';
import { GridSkeleton, RailSkeleton, RetryError } from '../components/Placeholders.js';
import {
  HERO_IDLE_MS,
  HERO_ROTATE_MS,
  advanceRotation,
  emptyRotation,
  finishedTitles,
  heroPool,
  randomSeed,
  rotationCurrent,
  settleRotation,
  type HeroRotation,
} from './home-hero.js';
import { seasonEpisodeLabel } from './title-labels.js';

/** Задержка перед отправкой поискового запроса на сервер, чтобы не слать запрос на каждое нажатие клавиши. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Что считается действием зрителя на главной: от них отсчитывается простой.
 * Прокрутку ловим на погружении — у рядов она своя и не всплывает.
 */
const ACTIVITY_EVENTS = ['pointermove', 'pointerdown', 'focusin', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Простой зрителя на главной: `true`, если ~10 с не было ни движения
 * курсора, ни фокуса, ни клавиш, ни прокрутки, ни касаний.
 *
 * Таймер один на всё: событие лишь запоминает время, а таймер, сработав,
 * сам доводит срок до конца, если действие было позже. Так частый
 * `pointermove` не пересоздаёт таймер на каждое движение. Возвращает ещё и
 * `poke` — отметить действие вручную (фокус карточки).
 */
function useIdle(): { idle: boolean; poke: () => void } {
  // Только что открытая главная — уже простой: зритель ещё ничего не выбрал.
  const [idle, setIdle] = useState(true);
  const lastActivity = useRef(0);
  const timer = useRef<number | null>(null);
  const poke = useCallback((): void => {
    lastActivity.current = Date.now();
    setIdle(false);
    if (timer.current !== null) return;
    const wait = (delay: number): void => {
      timer.current = window.setTimeout(() => {
        const left = lastActivity.current + HERO_IDLE_MS - Date.now();
        if (left > 0) {
          wait(left);
          return;
        }
        timer.current = null;
        setIdle(true);
      }, delay);
    };
    wait(HERO_IDLE_MS);
  }, []);
  useEffect(() => {
    const options = { capture: true, passive: true } as const;
    for (const type of ACTIVITY_EVENTS) document.addEventListener(type, poke, options);
    document.addEventListener('scroll', poke, options);
    return () => {
      for (const type of ACTIVITY_EVENTS) document.removeEventListener(type, poke, options);
      document.removeEventListener('scroll', poke, options);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = null;
    };
  }, [poke]);
  return { idle, poke };
}

/**
 * Сколько карточка должна пробыть в фокусе, прежде чем верх главной переключится
 * на её тайтл. Без паузы проход мышью через ряд перебирал бы фон на каждой
 * карточке — мельтешение вместо ответа на «что это».
 */
const HERO_FOCUS_MS = 250;

/**
 * За сколько до смены тайтла при автопоказе готовится следующий: его логотип
 * (ответ, картинка, тон) и кадр. Не сразу после смены — чтобы не делить сеть с
 * картинками только что вставшего тайтла, — но с запасом на медленную сеть.
 */
const HERO_PRELOAD_LEAD_MS = 12_000;

/**
 * Сколько верх ждёт ответа о логотипе, прежде чем выйти без названия. Дольше
 * держать верх пустым нельзя: при фокусе на карточке это читалось бы как
 * «сайт не отвечает».
 */
const HERO_TITLE_EARLY_MS = 400;

/** Пока результатов поиска нет: одна и та же ссылка, чтобы подложка не пересчитывалась. */
const NO_RESULTS: readonly CatalogItem[] = [];

type ContinueEntry = ContinueWatchingResponse['items'][number];

/** Что сцена показывает за тайтлом верха главной. */
function heroStageSource(item: CatalogItem): StageSource {
  return {
    key: item.id,
    // Слайдер главной — единственный ЧЁТКИЙ фон в приложении (правило
    // владельца): кадр 780 или 1280 точек по ширине экрана.
    art: stageArtImage(item.backdrop?.url, window.innerWidth, window.devicePixelRatio),
    tiny: tinyImage(item.poster?.url ?? item.backdrop?.url ?? null),
    poster: item.poster?.url ?? null,
  };
}

/**
 * Фон главной ниже слайдера — тот же мягкий расфокус, что на странице тайтла:
 * крупная копия кадра (нет кадра — постера), размытая один раз (`light`).
 */
function heroWashSource(item: CatalogItem): StageSource {
  return {
    key: item.id,
    art: null,
    tiny: tinyImage(item.poster?.url ?? item.backdrop?.url ?? null),
    wash: washImage(item.backdrop?.url, item.poster?.url),
  };
}

/** Зритель ниже слайдера? `visit` — который по счёту спуск: у каждого свой фон с чистого листа. */
interface Descent {
  below: boolean;
  visit: number;
}

/**
 * Ушёл ли зритель ниже слайдера — к рядам постеров.
 *
 * Следит наблюдатель за меткой посередине верха (`.home-stage-mark`): метка
 * ушла за верхний край экрана — зритель внизу. Обработчика прокрутки нет, и
 * раскладка на прокрутке не считается: наблюдатель срабатывает только в
 * момент, когда метка пересекает край. Где наблюдателя нет (старый браузер
 * телевизора), главная остаётся как была — с чётким фоном.
 */
function useDescent(): { markRef: (node: HTMLElement | null) => void; descent: Descent } {
  const [mark, setMark] = useState<HTMLElement | null>(null);
  const [descent, setDescent] = useState<Descent>({ below: false, visit: 0 });
  useEffect(() => {
    if (mark === null || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry === undefined) return;
      // Метка не видна и стоит выше экрана — её прокрутили. Не видна и стоит
      // ниже (низкий экран, высокий верх) — до неё ещё не дошли.
      const below = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      setDescent((previous) =>
        previous.below === below ? previous : { below, visit: previous.visit + (below ? 1 : 0) },
      );
    });
    observer.observe(mark);
    return () => {
      observer.disconnect();
      // Метки нет (открыт поиск) — «внизу» больше ничего не значит.
      setDescent((previous) => (previous.below ? { below: false, visit: previous.visit } : previous));
    };
  }, [mark]);
  return { markRef: setMark, descent };
}

const KIND_LABEL: Record<CatalogItem['kind'], string> = { movie: 'Фильм', tv: 'Сериал' };

/** «1 ч 20 мин» / «52 мин»: остаток так и говорят вслух. */
function formatLeft(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) return `${rest} мин`;
  return rest === 0 ? `${hours} ч` : `${hours} ч ${rest} мин`;
}

/**
 * Что сказать под кадром «Продолжить просмотр»: у сериала — какая серия, у
 * фильма — сколько осталось. Это ровно тот вопрос, с которым на полку смотрят.
 */
export function continueCaption(progress: ProgressEntry): string {
  if (progress.season !== null && progress.episode !== null) {
    return seasonEpisodeLabel(progress.season, progress.episode);
  }
  if (progress.durationMs !== null && progress.durationMs > progress.positionMs) {
    return `Осталось ${formatLeft(progress.durationMs - progress.positionMs)}`;
  }
  return `Просмотрено ${Math.round(progress.percent)} %`;
}

/**
 * Строка поиска со своим состоянием.
 *
 * Набранный текст живёт ЗДЕСЬ, а не в главной: иначе каждая буква перерисовывала
 * бы главную со всеми рядами. Наверх уходит только запрос, переживший паузу.
 */
function HomeSearch({ onSearch }: { onSearch: (query: string) => void }): React.JSX.Element {
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => onSearch(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, onSearch]);
  return <SearchField query={query} onQuery={setQuery} />;
}

/** Заглушка текста верха: пока нет тайтла или неизвестно, чем встанет его название. */
function HeroPlaceholder(): React.JSX.Element {
  return (
    <div className="hero-placeholder">
      <span className="skeleton-block hero-placeholder-name" />
      <span className="skeleton-block hero-placeholder-line is-short" />
      <span className="skeleton-block hero-placeholder-line" />
      <span className="skeleton-block hero-placeholder-line" />
      <span className="hero-placeholder-actions">
        <span className="skeleton-block hero-placeholder-button" />
        <span className="skeleton-block hero-placeholder-button" />
      </span>
    </div>
  );
}

/**
 * Верх главной — тайтл карточки в фокусе: название (логотипом, если он есть),
 * год и жанры, три строки описания и кнопки. Текст сменяется лесенкой — каждый
 * следующий блок чуть позже предыдущего, — поэтому смена читается как одно
 * движение, а не как мигание.
 */
const HomeHero = memo(function HomeHero({
  item,
  progress,
  slides,
  slide,
  onHold,
}: {
  item: CatalogItem;
  progress: ProgressEntry | null;
  /** Сколько тайтлов перелистывается наверху и который по счёту в круге сейчас (-1 — карточка в фокусе). */
  slides: number;
  slide: number;
  /** Курсор или фокус на самом верху — перелистывание ждёт. */
  onHold: (held: boolean) => void;
}): React.JSX.Element {
  const logo = useTitleLogo(item.id);
  // Текст верха выходит, когда известно, логотип у тайтла или текст: тогда
  // блок названия сразу встаёт по содержимому. Обычно ответ уже есть (его
  // просят при фокусе карточки и заранее — у автопоказа) или приходит за
  // десятки миллисекунд. Не пришёл за `HERO_TITLE_EARLY_MS` — верх выходит без
  // названия, а под него до конца показа держится полная высота (`held`):
  // строка с годом и описание не сдвигаются, когда название проявится.
  const pending = logo === undefined;
  const [heldId, setHeldId] = useState<string | null>(null);
  useEffect(() => {
    if (!pending) return undefined;
    const timer = window.setTimeout(() => setHeldId(item.id), HERO_TITLE_EARLY_MS);
    return () => window.clearTimeout(timer);
  }, [item.id, pending]);
  const titleHeld = heldId === item.id;
  const ready = !pending || titleHeld;
  // Самый первый тайтл ждёт на заглушке (она уже стоит, пока едет каталог);
  // при смене тайтла вместо заглушки — пауза: фон в это время растворяется.
  const shownOnce = useRef(false);
  useEffect(() => {
    if (ready) shownOnce.current = true;
  }, [ready]);
  const href = `#/title/${encodeURIComponent(item.id)}`;
  const meta = [
    item.releaseYear !== null ? String(item.releaseYear) : null,
    KIND_LABEL[item.kind],
    ...item.genres.slice(0, 2).map((genre) => genre.name),
  ].filter((part): part is string => part !== null);
  return (
    <section
      className="hero"
      aria-label={item.title}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') onHold(true);
      }}
      onPointerLeave={() => onHold(false)}
      onFocus={() => onHold(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) onHold(false);
      }}
    >
      {!ready && !shownOnce.current && <HeroPlaceholder />}
      {/* Ключ по тайтлу: новый тайтл — новые элементы, и лесенка появления
          проигрывается заново, без ручного управления анимацией. */}
      {ready && <div className="hero-copy" key={item.id}>
        <div className={titleHeld ? 'hero-title-slot is-held' : 'hero-title-slot'}>
          <TitleHeading title={item.title} logo={logo} className="hero-title" />
        </div>
        <p className="hero-meta">
          {meta.map((part) => (
            <span key={part}>{part}</span>
          ))}
          {item.ratings.average != null && <span className="hero-rating">★ {item.ratings.average.toFixed(1)}</span>}
          {progress !== null && <span className="hero-progress-note">{continueCaption(progress)}</span>}
        </p>
        {item.overview !== '' && <p className="hero-overview">{item.overview}</p>}
        <div className="hero-actions">
          {/* «Смотреть» ведёт туда же, куда «Смотреть онлайн» на карточке
              тайтла: к выбору источника, поиск которого запускает сам адрес.
              У начатого — одна главная кнопка, «Продолжить». */}
          <a className="primary-action hero-play" href={`${href}/sources/online`}>
            <PlayIcon className="action-icon" />
            {progress !== null ? 'Продолжить' : 'Смотреть'}
          </a>
          <a className="secondary-action" href={href}>
            Подробнее
          </a>
        </div>
      </div>}
      {/* Точки перелистывания — тихие, справа на уровне кнопок: подсказка
          «наверху сменяются тайтлы», а не управление. */}
      {ready && slides > 1 && (
        <span className="hero-dots" aria-hidden="true">
          {Array.from({ length: slides }, (_, index) => (
            <i key={index} className={index === slide ? 'is-active' : undefined} />
          ))}
        </span>
      )}
    </section>
  );
});

/**
 * Полка «Продолжить просмотр»: кадры 16:9 с полосой прогресса и подписью «что
 * дальше». Отдельный компонент под `memo`: смена тайтла наверху её не трогает.
 */
const ContinueRail = memo(function ContinueRail({
  entries,
  reload,
  onFocusItem,
}: {
  entries: ContinueEntry[];
  reload: () => void;
  onFocusItem: (item: CatalogItem, via: InputVia) => void;
}): React.JSX.Element {
  return (
    <section className="catalog-section">
      <h2>Продолжить просмотр</h2>
      <div className="rail rail--wide">
        {entries.map(({ item, progress }) => (
          <Card
            key={item.id}
            item={item}
            wide
            caption={continueCaption(progress)}
            progressPercent={progress.percent}
            onFocusItem={onFocusItem}
            // Долгое нажатие на кадр: убрать из списка, начать заново или
            // закрыть тайтл. Полку перечитываем целиком — все три действия
            // меняют не одну карточку, а состав «продолжить».
            actions={{
              onRemove: () => void removeFromHistory(item.id).then(reload),
              onReset: () => void resetProgress(item.id).then(reload),
              onWatched: () => void markWatched(item.id).then(reload),
            }}
          />
        ))}
      </div>
    </section>
  );
});

/** Обычный ряд постеров. `memo` — по той же причине, что и у полки выше. */
const CatalogRail = memo(function CatalogRail({
  title,
  items,
  onFocusItem,
}: {
  title: string;
  items: CatalogItem[];
  onFocusItem: (item: CatalogItem, via: InputVia) => void;
}): React.JSX.Element {
  return (
    <section className="catalog-section">
      <h2>{title}</h2>
      <div className="rail">
        {items.map((item) => (
          <Card key={item.id} item={item} onFocusItem={onFocusItem} />
        ))}
      </div>
    </section>
  );
});

export function HomePage(): React.JSX.Element {
  const [catalog, setCatalog] = useState<CatalogHomeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Попытка загрузки каталога: «Повторить» после ошибки заводит её заново. */
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [continueItems, setContinueItems] = useState<ContinueEntry[]>([]);
  /** Полка хоть раз прочитана: до этого «на полке нет» ещё ничего не значит. */
  const [shelfKnown, setShelfKnown] = useState(false);

  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResult, setSearchResult] = useState<SearchResponse | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // «Продолжить просмотр» — своя подборка сверху. Тихо игнорируем ошибку:
  // раздел просто не покажется, каталог от этого не должен падать.
  //
  // Возвращает, идёт ли на сервере сверка с медиатекой, и номер последней
  // завершённой: полка приходит сразу с тем, что известно, а подтянутое из
  // Jellyfin появится через несколько секунд — и увидеть его зритель должен,
  // не уходя с экрана.
  const reloadContinue = React.useCallback(async (signal?: AbortSignal): Promise<{ syncing: boolean; revision: number }> => {
    try {
      const response = await getContinueWatching(signal);
      setContinueItems(response.items);
      setShelfKnown(true);
      return { syncing: response.syncing === true, revision: response.syncRevision ?? 0 };
    } catch {
      if (signal?.aborted !== true) setContinueItems([]);
      return { syncing: false, revision: 0 };
    }
  }, []);
  // Обёртка в стрелку не косметика: `reloadContinue` принимает сигнал отмены,
  // а `.then(reloadContinue)` передал бы туда ответ предыдущего запроса.
  const reloadShelf = useCallback((): void => {
    void reloadContinue();
  }, [reloadContinue]);

  useEffect(() => {
    setError(null);
    getHomeCatalog()
      .then(setCatalog)
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : 'Ошибка загрузки'),
      );
  }, [catalogAttempt]);

  /**
   * Перечитывать полку при каждом ВОЗВРАЩЕНИИ на главную, а не только при
   * первом открытии.
   *
   * Зритель уходит смотреть и возвращается назад, сворачивает вкладку,
   * отвлекается на другую — и всё это время полка показывает то, что было до
   * ухода, хотя позиция уже уехала на сервер. Тот же приём, что на карточке
   * тайтла: `pageshow` здесь не лишний — возврат из bfcache (кнопка «назад» в
   * браузере) не поднимает ни `visibilitychange`, ни `focus`, а страница при
   * этом та же самая.
   */
  const [visitGeneration, setVisitGeneration] = useState(0);
  useEffect(() => {
    const refresh = (): void => {
      if (document.visibilityState === 'hidden') return;
      setVisitGeneration((value) => value + 1);
    };
    window.addEventListener('focus', refresh);
    window.addEventListener('pageshow', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      window.removeEventListener('pageshow', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  useEffect(() => {
    // Уход с главной обязан снимать ожидание: запрос ждёт конца сверки и живёт
    // секунды, а вернувшийся ответ дописал бы полку уже закрытому экрану.
    const controller = new AbortController();
    void (async () => {
      // Сначала показываем то, что есть, — зритель не ждёт чужой сервис.
      const { syncing, revision } = await reloadContinue(controller.signal);
      if (!syncing || controller.signal.aborted) return;
      // Сверка идёт: дожидаемся её и перечитываем полку. Номер обязателен —
      // обход медиатеки, стоящей рядом, укладывается в миллисекунды и успевает
      // закончиться раньше, чем уйдёт этот запрос; по номеру сервер видит, что
      // сверка уже была, и отвечает сразу. Ждём ОДИН раз: следующее открытие
      // главной начнёт всё заново.
      const { changed } = await awaitContinueWatchingSync(revision, controller.signal)
        .catch(() => ({ changed: false }));
      if (!changed || controller.signal.aborted) return;
      await reloadContinue(controller.signal);
    })();
    return () => controller.abort();
  }, [reloadContinue, visitGeneration]);

  useEffect(() => {
    if (!debouncedQuery) {
      setSearchResult(null);
      setSearchError(null);
      setSearchLoading(false);
      return;
    }
    const controller = new AbortController();
    setSearchLoading(true);
    setSearchError(null);
    search(debouncedQuery, 1, controller.signal)
      .then(setSearchResult)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return;
        setSearchError(e instanceof Error ? e.message : 'Ошибка поиска');
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearchLoading(false);
      });
    return () => controller.abort();
  }, [debouncedQuery]);

  const loadMoreResults = (): void => {
    if (!searchResult) return;
    setSearchLoadingMore(true);
    search(debouncedQuery, searchResult.page + 1)
      .then((response) =>
        setSearchResult((prev) =>
          prev ? { ...response, items: [...prev.items, ...response.items] } : response,
        ),
      )
      .catch((e: unknown) => setSearchError(e instanceof Error ? e.message : 'Ошибка поиска'))
      .finally(() => setSearchLoadingMore(false));
  };

  const sections = catalog?.sections ?? [];
  const rails = useMemo(() => sections.filter((section) => section.layout !== 'hero'), [sections]);

  /**
   * Что стоит наверху (правило владельца).
   *
   * Пока зритель ничего не трогает (простой ~10 с), верх сам показывает
   * тайтлы из «Сейчас смотрят», «Популярных фильмов», «Популярных сериалов»
   * и полки «Продолжить просмотр» в случайном порядке: круг без повторов, после круга — новое
   * перемешивание, и один тайтл два раза подряд не стоит. Смена — раз в 24 с.
   * Зритель водит фокус или курсор по карточкам — верх сразу (через 250 мс)
   * показывает карточку в фокусе, перелистывание стоит. Снова простой —
   * верх возвращается к случайному показу с тайтла, отличного от того, что
   * на экране. Перелистывание ждёт, пока курсор или фокус на самом верху и
   * пока вкладка скрыта, и не включается вовсе, если просили меньше движения.
   * Досмотренное наверх не попадает — ни фильм, ни сериал, в котором смотреть
   * больше нечего; телепередачи (ток-шоу, реалити, новости) — тоже.
   */
  const [history, setHistory] = useState<readonly ProgressEntry[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    getHistory()
      .then((response) => {
        if (!controller.signal.aborted) setHistory(response.entries);
      })
      // История — уточнение, а не условие: без неё верх просто не отсеет досмотренное.
      .catch(() => undefined);
    return () => controller.abort();
  }, []);
  /** Зерно случайного порядка — одно на заход на главную. */
  const [seed] = useState(randomSeed);
  const shelfItems = useMemo(() => continueItems.map((entry) => entry.item), [continueItems]);
  // Досмотрен ли сериал, видно только вместе с полкой: сериал с досмотренной
  // серией, у которого есть следующая, сервер держит на ней.
  const finished = useMemo(
    () => finishedTitles(history, shelfKnown ? new Set(shelfItems.map((item) => item.id)) : null),
    [history, shelfKnown, shelfItems],
  );
  const pool = useMemo(() => heroPool(sections, shelfItems, finished, seed), [sections, shelfItems, finished, seed]);
  // Набор сравниваем по составу, а не по ссылке: перечитанная полка с тем же
  // составом не должна сбрасывать срок текущего тайтла.
  const poolKey = pool.map((item) => item.id).join('\n');
  const poolIds = useMemo(() => (poolKey === '' ? [] : poolKey.split('\n')), [poolKey]);
  const [storedRotation, setRotation] = useState<HeroRotation>(() => emptyRotation(seed));
  // Круг приводим к набору прямо при отрисовке: набор меняется (приехала
  // полка, история отсеяла досмотренное), а пустого или чужого тайтла
  // наверху не должно мелькнуть ни на кадр.
  const rotation = settleRotation(storedRotation, poolIds);
  const currentId = rotationCurrent(rotation, poolIds);
  const [focused, setFocused] = useState<CatalogItem | null>(null);
  /** Курсор или фокус на самом верху — перелистывание стоит. */
  const [heroHeld, setHeroHeld] = useState(false);
  const [hidden, setHidden] = useState(() => document.visibilityState === 'hidden');
  const [calm] = useState(() => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  const { idle, poke } = useIdle();
  const focusTimer = useRef<number | null>(null);

  /**
   * Карточка, до которой дошли КЛАВИАТУРОЙ ниже слайдера: за ней идёт мягкий
   * фон. `visit` — в какой спуск: в следующий спуск фон снова начнёт с тайтла
   * слайдера.
   */
  const [keyed, setKeyed] = useState<{ visit: number; item: CatalogItem } | null>(null);
  const visitRef = useRef(0);

  const onFocusItem = useCallback((item: CatalogItem, via: InputVia = 'pointer'): void => {
    // Логотип и цвет просим сразу, не дожидаясь паузы: к моменту, когда верх
    // переключится, они, скорее всего, уже приедут, и название не сменится с
    // текста на логотип у зрителя на глазах.
    void loadTitleLogo(item.id);
    poke();
    if (focusTimer.current !== null) window.clearTimeout(focusTimer.current);
    focusTimer.current = window.setTimeout(() => {
      focusTimer.current = null;
      setFocused(item);
      // Мягкий фон внизу ведёт только клавиатура (пульт): за курсором и
      // пальцем он не ходит — иначе страница «размывается» под мышью.
      if (via === 'keyboard') setKeyed({ visit: visitRef.current, item });
    }, HERO_FOCUS_MS);
  }, [poke]);
  useEffect(() => () => {
    if (focusTimer.current !== null) window.clearTimeout(focusTimer.current);
  }, []);
  useEffect(() => {
    const onVisibility = (): void => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  /**
   * Ниже слайдера фон — мягкий расфокус тайтла, который стоял наверху В
   * МОМЕНТ СПУСКА, неподвижным слоем на весь экран; он держится, пока зритель
   * внизу. Наведение мыши на карточки его не меняет (правка владельца
   * 05.10.2026: «страница не должна размываться при наведении мышкой») — сам
   * слайдер за курсором идёт по-прежнему, и, вернувшись наверх, зритель видит
   * чёткий кадр того, на что наводил последним. Фокус с клавиатуры (пульт)
   * ведёт фон за собой, как в приложении. Наверху фон по-прежнему чёткий.
   *
   * Мягкий фон включается не в миг пересечения метки, а когда его картинка
   * готова (`washShown`): до тех пор стоит чёткий, и смена идёт растворением
   * одного в другое, а не через пустой фон. На каждый спуск сцена мягкого
   * фона создаётся заново (`key`): в ней нет картинки прошлого спуска, и
   * первый тайтл проявляется из ничего, а не сквозь старый.
   */
  const { markRef, descent } = useDescent();
  const below = descent.below;
  visitRef.current = descent.visit;
  const [washShown, setWashShown] = useState(0);
  const washOn = below && washShown === descent.visit;

  const shownId = focused?.id ?? currentId;
  // Слайдер за краем экрана не листается: фон внизу меняет только зритель —
  // карточкой в фокусе с клавиатуры, — а кадры зря не качаются.
  const canRotate = idle && !calm && !hidden && !heroHeld && !below && pool.length > 0;
  // Перелистывание: таймер заводится заново после каждой смены и каждой
  // паузы — следующий тайтл всегда получает полный срок. Стоит карточка из
  // фокуса, а простой уже наступил — к случайному показу возвращаемся сразу.
  useEffect(() => {
    if (!canRotate) return undefined;
    if (focused === null && pool.length < 2) return undefined;
    const timer = window.setTimeout(() => {
      setFocused(null);
      setRotation((previous) => advanceRotation(settleRotation(previous, poolIds), poolIds, shownId));
    }, focused !== null ? 0 : HERO_ROTATE_MS);
    return () => window.clearTimeout(timer);
  }, [canRotate, focused, pool.length, poolIds, shownId]);
  // Следующий тайтл автопоказа готовится заранее: ответ о логотипе, его
  // картинка и тон, кадр и цвет постера. К смене всё уже лежит в кэше, и верх
  // меняется целиком и сразу — без названия, появляющегося вдогонку.
  const upcoming = pool.length > 1 ? rotationCurrent(advanceRotation(rotation, poolIds, shownId), poolIds) : null;
  useEffect(() => {
    // Ответ о логотипе лёгкий — его просим сразу; он пригодится и тогда, когда
    // к автопоказу вернутся с карточки в фокусе, без срока на подготовку.
    if (upcoming !== null) void loadTitleLogo(upcoming);
  }, [upcoming]);
  useEffect(() => {
    // Картинки — только пока верх и правда листает сам: при живом зрителе
    // следующий тайтл выбирает он, и качать кадр «на всякий случай» незачем.
    if (upcoming === null || !canRotate || focused !== null) return undefined;
    const next = pool.find((item) => item.id === upcoming);
    if (next === undefined) return undefined;
    const timer = window.setTimeout(() => {
      preloadTitleLogo(next.id);
      preloadStage(heroStageSource(next));
    }, HERO_ROTATE_MS - HERO_PRELOAD_LEAD_MS);
    return () => window.clearTimeout(timer);
    // `pool` в зависимостях нет нарочно: состав набора уже отражён в `upcoming`,
    // а новая ссылка на тот же состав (перечитанная полка) не должна заводить
    // подготовку заново.
  }, [upcoming, canRotate, focused]);

  const fallbackHero =
    (sections.find((s) => s.layout === 'hero' && s.items.length > 0) ?? sections.find((s) => s.items.length > 0))
      ?.items[0] ??
    null;
  const heroItem = focused ?? pool.find((item) => item.id === currentId) ?? fallbackHero;
  const heroSlide = focused === null && currentId !== null
    ? rotation.round.filter((id) => poolIds.includes(id)).indexOf(currentId)
    : -1;
  const heroProgress = heroItem === null
    ? null
    : (continueItems.find((entry) => entry.item.id === heroItem.id)?.progress ?? null);
  const stageSource = useMemo<StageSource | null>(
    () => (heroItem === null ? null : heroStageSource(heroItem)),
    [heroItem],
  );
  // Тайтл мягкого фона: закреплён в момент спуска. Запоминаем прямо при
  // отрисовке, а не эффектом: иначе на первом кадре спуска фон начал бы
  // грузиться для тайтла прошлого спуска.
  const pinned = useRef<{ visit: number; item: CatalogItem | null }>({ visit: 0, item: null });
  if (below && (pinned.current.visit !== descent.visit || pinned.current.item === null)) {
    pinned.current = { visit: descent.visit, item: heroItem };
  }
  const washItem = !below
    ? null
    : keyed !== null && keyed.visit === descent.visit ? keyed.item : pinned.current.item;
  const washSource = useMemo<StageSource | null>(
    () => (washItem === null ? null : heroWashSource(washItem)),
    [washItem],
  );

  const isSearching = debouncedQuery.length > 0;
  const { washItem: resultWash, onFocusItem: onFocusResult, onLeaveItem: onLeaveResult } = useWashItem(searchResult?.items ?? NO_RESULTS);
  const canLoadMoreResults =
    !!searchResult && !searchLoading && searchResult.page < searchResult.totalPages;
  const searchSentinelRef = useInfiniteScroll(loadMoreResults, {
    enabled: canLoadMoreResults && !searchLoadingMore,
    rearmKey: searchResult?.items.length ?? 0,
  });

  return (
    <main className={['app home-page', isSearching ? 'is-searching' : '', washOn ? 'is-below' : ''].filter(Boolean).join(' ')}>
      {/* Результаты поиска — обычный список: подложка раздела от карточки под
          курсором или в фокусе (иначе — первой). */}
      {isSearching && <SectionWash item={resultWash} />}
      {/* Мягкий фон — первым в странице: слой прилипает к верху экрана и
          стоит на месте, пока страница едет. Наверху он не нужен вовсе, а
          после возврата наверх гаснет и стоит, ничего не загружая. */}
      {!isSearching && descent.visit > 0 && (
        <TitleStage
          key={descent.visit}
          source={below ? washSource : null}
          className="home-wash"
          soft
          onShown={() => setWashShown(descent.visit)}
        />
      )}
      {/* Внизу чёткая сцена стоит как есть: её не видно, и кадры карточек под
          курсором зря не качаются. Вернулись наверх — догоняет тайтл слайдера. */}
      {!isSearching && <TitleStage source={below ? null : stageSource} className="home-stage" />}
      {!isSearching && <span ref={markRef} className="home-stage-mark" aria-hidden="true" />}

      <header className="header home-header">
        <span className="brand">
          <LumosMark className="brand-mark" />
          Lumos
        </span>
        <HomeSearch onSearch={setDebouncedQuery} />
      </header>

      {!isSearching && heroItem !== null && (
        <HomeHero item={heroItem} progress={heroProgress} slides={calm ? 0 : pool.length} slide={heroSlide} onHold={setHeroHeld} />
      )}
      {/* Пока тайтла для верха нет — заглушка той же высоты: пустого верха не
          бывает, и ряды не прыгают вверх-вниз, когда каталог приедет. */}
      {!isSearching && heroItem === null && catalog === null && !error && (
        <section className="hero" aria-hidden="true">
          <HeroPlaceholder />
        </section>
      )}

      {!isSearching && error && (
        <RetryError message={error} onRetry={() => setCatalogAttempt((value) => value + 1)} />
      )}
      {!isSearching && !catalog && !error && (
        <>
          <RailSkeleton />
          <RailSkeleton />
        </>
      )}
      {!isSearching && catalog?.notice && <p className="catalog-notice">{catalog.notice}</p>}
      {!isSearching && catalog !== null && heroItem === null && (
        <div className="empty-state" role="status">
          <h2>Каталог пока пуст</h2>
          <p>Как только витрина ответит, здесь появятся фильмы и сериалы. Найти нужное можно поиском выше.</p>
        </div>
      )}

      {!isSearching && continueItems.length > 0 && (
        <ContinueRail entries={continueItems} reload={reloadShelf} onFocusItem={onFocusItem} />
      )}

      {!isSearching &&
        // Секцию-витрину рядом не рисуем: в ней один тайтл, и он уже стоит
        // наверху. Ряд из одной карточки — это не ряд, а недоразумение.
        rails.map((section) => (
          <CatalogRail
            key={section.id}
            title={section.title}
            items={section.items}
            onFocusItem={onFocusItem}
          />
        ))}

      {isSearching && (
        <section className="catalog-section search-results">
          <h2>Результаты поиска «{debouncedQuery}»</h2>

          {searchError && <div className="state error">{searchError}</div>}
          {searchLoading && !searchResult && <GridSkeleton />}
          {searchResult?.notice && <p className="catalog-notice">{searchResult.notice}</p>}

          {searchResult && searchResult.items.length === 0 && !searchLoading && (
            <p className="state">Ничего не найдено.</p>
          )}

          {searchResult && searchResult.items.length > 0 && (
            <>
              <div className="grid">
                {searchResult.items.map((item: CatalogItem) => (
                  <Card key={item.id} item={item} onFocusItem={onFocusResult} onLeaveItem={onLeaveResult} />
                ))}
              </div>

              {canLoadMoreResults && (
                <div ref={searchSentinelRef} className="scroll-sentinel" aria-hidden="true" />
              )}
              {/* Следующая страница результатов — знак посередине её будущей
                  строки; высота строки занята заранее. */}
              {searchLoadingMore && <Loader variant="row" label="Загрузка…" className="grid-more-loader" />}
            </>
          )}
        </section>
      )}
    </main>
  );
}
